import { FakeInput } from "@similarweb/ui-components";
import { ListItemWebsite } from "@similarweb/ui-components";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { i18nFilter } from "app/filters/i18nFilter";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import type { ISelectedDomain } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { ESelectedDomainErrorType } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import {
    FakeInputContainerStyled,
    ErrorLabel,
    ErrorBorderColorMap,
} from "app/pages/segments/redesigned-wizard/segment-wizard/styledComponents";
import { ChangeWebsiteModal } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-modals/ChangeWebsiteModal";
import { useSegmentWizardContext } from "app/pages/segments/redesigned-wizard/segment-wizard/SegmentWizardContextProvider";
import type { IconName } from "@similarweb/icons";

const ERROR_ICON_NAME = "alert-circle";

export const SelectedDomain: FC<ISelectedDomain> = ({ onClearMainSite, selectedSite, isSelectedSiteAllowed }) => {
    const { name, image, validation } = selectedSite;
    const { segmentRules } = useSegmentWizardContext();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    let errorType: ESelectedDomainErrorType;
    let errorMsg: string;
    let errorIconName: IconName;
    let errorIconColor: string;
    const isMidTierUser = SegmentsUtils.isMidTierUser();
    if (!isSelectedSiteAllowed) {
        if (validation.isTooSmall) {
            errorType = ESelectedDomainErrorType.NOT_ENOUGH_DATA;
            errorMsg = i18nFilter()("workspaces.autocomplete.wizard.domain.input.error.too.small.txt.label");
        } else if (isMidTierUser && !validation.isRobotsTxt) {
            errorType = ESelectedDomainErrorType.UPGRADE_ERROR;
            errorMsg = i18nFilter()("workspaces.autocomplete.new.wizard.domain.input.error.robots.txt.label");
        } else if (validation.isInvalid || validation.isInvalid === undefined) {
            errorType = ESelectedDomainErrorType.SENSITIVE_DATA;
            errorMsg = i18nFilter()("workspaces.autocomplete.wizard.domain.invalid.input.error.label");
        }
        if (errorType) {
            // If errorType exists at this point it means one of the above errors occured
            errorIconName = ERROR_ICON_NAME;
            errorIconColor = ErrorBorderColorMap[errorType];
        }
    } else if (!validation.isRobotsTxt) {
        errorType = ESelectedDomainErrorType.WARNING;
        errorMsg = i18nFilter()("workspaces.autocomplete.wizard.domain.hide_urls_preview.input.warning.label");
    }

    const doRulesExist = useMemo(() => segmentRules.length > 2, [segmentRules.length]);

    const showConfirmDeleteModal = useCallback(() => {
        if (
            errorType !== ESelectedDomainErrorType.NOT_ENOUGH_DATA &&
            errorType !== ESelectedDomainErrorType.SENSITIVE_DATA &&
            doRulesExist
        ) {
            setShowConfirmModal(true);
        } else {
            onClearMainSite();
        }
    }, [errorType, onClearMainSite, doRulesExist]);

    const handleDeleteCancellation = useCallback(() => {
        setShowConfirmModal(false);
    }, []);

    const handleDelete = useCallback(() => {
        setShowConfirmModal(false);
        onClearMainSite();
    }, [onClearMainSite]);

    return (
        <>
            <FakeInputContainerStyled errorType={errorType}>
                <FakeInput
                    onClear={showConfirmDeleteModal}
                    errorIconName={errorIconName}
                    errorIconColor={errorIconColor}
                >
                    <ListItemWebsite
                        text={name}
                        img={image}
                    />
                </FakeInput>
            </FakeInputContainerStyled>
            {errorMsg && <ErrorLabel errorType={errorType}>{errorMsg}</ErrorLabel>}
            <ChangeWebsiteModal
                isOpen={showConfirmModal}
                onModalClose={handleDeleteCancellation}
                onConfirm={handleDelete}
            />
        </>
    );
};
